/* eslint-disable no-nested-ternary */
import { FC, useState, useRef } from 'react';
import { Button, Divider, Icon } from 'common-components';
import classNames from 'classnames';
import CredentialCardProps from './credentialCardTypes';
import styles from './credentialCard.module.scss';
import { useTranslationContext } from '../../contexts/translationContext';
import ActionsMenu from './actionsMenu/actionsMenu';
import CredentialsCreationCard from '../credentialsCreationCard/credentialsCreationCard';
import { CreationData, DateTypes } from '../credentialsCreationCard/credentialsCreationCardTypes';
import HiddenText from '../hiddenText/hiddenText';
import CopyToClipboard from '../copyToClipboard/copyToClipboard';
import { useOuterClick } from '../../utils';

const CredentialCard: FC<CredentialCardProps> = (props: CredentialCardProps) => {
    const {
        credential,
        isAuthorized = false,
        isRestricted = false,
        identifyHandler,
        logoutHandler,
        deleteHandler,
        updateHandler,
        renewHandler,
        containerStyle = {}
    } = props;

    const [showCredentials, setShowCredentials] = useState<boolean>(false);
    const [showActionsMenu, setShowActionsMenu] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [copiedValue, setCopiedValue] = useState<string | null>(null);
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);

    const actionsRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const { translate } = useTranslationContext();

    const AUTHORIZE = translate('Authorize');
    const AUTHORIZED = translate('Authorized');
    const IDENTIFT_AS = translate('IdentifyAs');
    const LOG_OUT = translate('LogOut');
    const CREDENTIALS = translate('Credentials');
    const BACK = translate('Back');
    const VALID_FOR = translate('ValidFor');
    const KEY = translate('Key');
    const TOKEN = translate('Token');
    const NEW = translate('New');
    const EXPIRED = translate('Expired');
    const RENEW = translate('Renew');
    const UNAUTHORIZED = translate('Unauthorized');

    const IDENTIFY_TEXT = credential.isExpired ? RENEW : isAuthorized ? LOG_OUT : IDENTIFT_AS;
    const CREDENTIALS_TEXT = showCredentials ? BACK : CREDENTIALS;

    useOuterClick(actionsRef, () => setShowActionsMenu(false));

    let expirationDateType = credential.expiration.match(/[a-zA-Z]+/)?.[0];

    const findKeyByValue = (obj: { [key: string]: any }, value: any) => {
        const entry = Object.entries(obj).find(([, val]) => val === value);
        return entry ? entry[0] : null;
    };

    expirationDateType = findKeyByValue(DateTypes, expirationDateType) || 'Days';

    const expirationAmount = Number(credential.expiration.slice(0, -1));

    const onUpdate = (data: CreationData) => {
        updateHandler(credential.id, data.name, `${data.amount}${data.dateType}`);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div>
                <CredentialsCreationCard
                    createHandler={onUpdate}
                    isEdit={true}
                    credentialName={credential.name}
                    expirationDateType={expirationDateType}
                    expirationAmount={expirationAmount}
                    onBack={() => setIsEditing(false)}
                />
            </div>
        );
    }

    const onKebabMenuClick = () => {
        setShowActionsMenu(!showActionsMenu);
    };

    const credentialsView = () => {
        return (
            <div className={styles['credentials-container']}>
                <div
                    className={styles.row}
                    onMouseEnter={() => setHoveredKey(credential.apiKey)}
                    onMouseLeave={() => setHoveredKey(null)}
                >
                    <span className={styles['key-title']}>{KEY}</span>
                    <HiddenText isHidden={true} text={credential.apiKey} />
                    <CopyToClipboard
                        text={credential.apiKey}
                        isCopied={copiedValue == credential.apiKey}
                        onCopy={setCopiedValue}
                        isVisible={hoveredKey == credential.apiKey}
                    />
                </div>
                <Divider orientation="horizontal" color="rgba(98, 98, 98, 0.20)" />
                <div
                    className={styles.row}
                    onMouseEnter={() => setHoveredKey(credential.encryptedToken)}
                    onMouseLeave={() => setHoveredKey(null)}
                >
                    <span className={styles['key-title']}>{TOKEN}</span>
                    <HiddenText isHidden={true} text={credential.encryptedToken} />
                    <CopyToClipboard
                        text={credential.encryptedToken}
                        isCopied={copiedValue == credential.encryptedToken}
                        onCopy={setCopiedValue}
                        isVisible={hoveredKey == credential.encryptedToken}
                    />
                </div>
            </div>
        );
    };

    return (
        <div
            className={classNames(
                styles.container,
                isAuthorized && styles['authorized-container'],
                credential.isExpired && styles['expired-container']
            )}
            style={containerStyle}
        >
            {
                <div className={classNames(styles['authorized-banner'], !isAuthorized && styles.hidden)}>
                    <Icon name={'checkAdmin'} className={styles['check-icon-wrapper']} />
                    <span>{isAuthorized ? AUTHORIZED : AUTHORIZE}</span>
                </div>
            }
            <div className={styles.content}>
                <div className={styles.title}>{credential.name}</div>
                <div className={styles.expiration}>
                    <span>
                        {VALID_FOR} {expirationAmount} {expirationDateType}
                    </span>
                </div>
                <div className={styles.icon}>
                    <Icon
                        name={isAuthorized ? 'authKeysUnlocked' : 'authKeys'}
                        isPng={true}
                        style={{ width: '3.4rem' }}
                    />
                </div>
                {showCredentials ? (
                    credentialsView()
                ) : (
                    <div style={{ opacity: isRestricted ? 0.4 : 1, pointerEvents: isRestricted ? 'none' : 'auto' }}>
                        <Button
                            type={'learningPanelAdminActionButton'}
                            text={IDENTIFY_TEXT}
                            onClick={() => {
                                credential.isExpired
                                    ? renewHandler(credential.id)
                                    : isAuthorized
                                    ? logoutHandler()
                                    : identifyHandler(credential);
                            }}
                            className={styles['credentials-button-cx']}
                            lowerCase={true}
                            startIcon={<Icon name={'usersEvent'} style={{ display: 'flex', width: '1.2rem' }} />}
                        />
                    </div>
                )}
                <div className={styles['button-wrapper']}>
                    <div style={{ opacity: isRestricted ? 0.4 : 1, pointerEvents: isRestricted ? 'none' : 'auto' }}>
                        <Button
                            type={'learningPanelAdminOnlyText'}
                            text={CREDENTIALS_TEXT}
                            onClick={() => {
                                !credential.isExpired && setShowCredentials(!showCredentials);
                            }}
                            lowerCase={true}
                            className={styles['credentials-button-cx']}
                            startIcon={showCredentials && <Icon name={'adminArrowLeft'} style={{ display: 'flex' }} />}
                            endIcon={
                                !showCredentials && (
                                    <Icon
                                        name={'adminArrowLeft'}
                                        style={{ transform: 'rotate(180deg)', display: 'flex' }}
                                    />
                                )
                            }
                        />
                    </div>
                </div>
            </div>

            <div className={styles.actions} ref={actionsRef}>
                <div style={{ opacity: isRestricted ? 0.4 : 1, pointerEvents: isRestricted ? 'none' : 'auto' }}>
                    <Button
                        type={'learningPanelAdminActionButtonIcon'}
                        onClick={onKebabMenuClick}
                        lowerCase={true}
                        startIcon={<Icon name={'KebabMenuAdmin'} />}
                    />
                </div>
                <div className={styles['actions-manu-container']}>
                    {showActionsMenu && !isRestricted && (
                        <ActionsMenu onDelete={() => deleteHandler(credential.id)} onEdit={() => setIsEditing(true)} />
                    )}
                </div>
            </div>

            {credential.isExpired ? (
                <div className={styles['expired-tag']}>{EXPIRED}</div>
            ) : isRestricted ? (
                <div className={styles['expired-tag']}>{UNAUTHORIZED}</div>
            ) : (
                credential.isNew && <div className={styles.tag}>{NEW}</div>
            )}
        </div>
    );
};

export default CredentialCard;
